/**
 * ENG-8574: the host-side tool-call scan loop.
 *
 * Ties slice 1's extractor to ENG-8426's ingest under the cursor contract in
 * `tool-call-cursor.ts`. Mirrors `maybeExtractMemories` (ENG-6200): fired
 * best-effort from the per-agent manager tick, never throwing into it, and
 * keeping the transcript on the host — only metadata and a redacted target
 * cross the wire.
 *
 * THREE BOUNDS THAT ARE NOT OPTIONAL.
 *
 * 1. THE FIRST-RUN STORM. On first arming, every transcript already on the host
 *    is unscanned. Two prod hosts hold ~560 MB of un-swept transcripts, most of
 *    it older than the retention window it would be reported against. A naive
 *    first scan would emit six figures of rows in one burst. So a file seen for
 *    the first time starts at its CURRENT END, not at byte zero, and the
 *    coverage row records `started_from_line`/`started_from_byte` so the skipped
 *    prefix is explained rather than an unexplained gap. Backfill is opt-in.
 *
 * 2. THE BATCH CAP. The route rejects a batch over 500 with a 400, which is
 *    non-retryable — so an oversize batch is not a delay, it is data loss with
 *    a log line. Chunking happens here, and the constant is pinned to the
 *    route's by test.
 *
 * 3. THE WINDOW BUDGET (ENG-8740). Bound 1 is a bound on the FIRST scan of a
 *    file, and it is the only one an operator chooses. It does not cover
 *    `backfill: true`, and — the reason this bound had to exist — it does not
 *    cover the REWIND: a rotated or truncated transcript resets the read to byte
 *    0 by itself, with nobody asking. `extractTranscriptWindow` therefore
 *    consumes at most `WINDOW_BUDGET_BYTES` per call and reports `truncated`,
 *    and the scan simply resumes on the next tick. A backlog is drained over
 *    many ticks rather than in one synchronous read that blocks the manager and
 *    holds the whole file in memory.
 */

import { statSync } from 'node:fs';

import { ApiError } from './api-client.js';
import { readHostArchiveAddress } from './host-archive-address.js';
import {
  EXTRACTOR_VERSION,
  enumerateSessionTranscripts,
  extractTranscriptWindow,
  type ExtractOptions,
  type ToolCallRecord,
} from './tool-call-extractor.js';
import { REDACTION_RULE_VERSION } from './tool-call-redaction.js';
import {
  coverageRowFor,
  cursorKey,
  cursorStatePath,
  initialCursor,
  loadCursors,
  nextCursor,
  saveCursors,
  type CoverageRow,
  type FileCursor,
  type IngestOutcome,
} from './tool-call-cursor.js';

/**
 * MUST equal MAX_TOOL_CALLS_PER_POST in packages/api/src/routes/host-runtime.ts.
 * Over the cap the route answers 400, which the cursor contract treats as
 * non-retryable — so a drift here does not slow ingestion down, it drops the
 * batch. Pinned by test.
 */
export const MAX_CALLS_PER_POST = 500;

/** Coverage rows per POST. Must equal MAX_COVERAGE_ROWS_PER_POST in the route. */
export const MAX_COVERAGE_ROWS_PER_POST = 200;

/** Minimal surface the scanner needs, so tests need no HTTP. */
export interface ScanApi {
  post<T = Record<string, unknown>>(path: string, body?: unknown): Promise<T>;
}

export interface ScanSummary {
  filesScanned: number;
  callsExtracted: number;
  callsIngested: number;
  /** Files whose cursor did NOT move because ingest failed. */
  filesHeld: number;
  coverageReported: number;
  /**
   * The route answered a definitive `entitled: false` on at least one batch
   * this scan (ENG-8575).
   *
   * Reported explicitly rather than left to be inferred from the counters
   * above, because every inference available from out there is wrong in some
   * reachable case: "extracted > 0 and ingested === 0" is also true when every
   * row was a duplicate, and "filesHeld === 0" is also true when there was
   * simply nothing to send. The caller latches an efficiency skip on this, so
   * a false positive stops collection for an entitled customer — which is the
   * unrecoverable direction.
   */
  notEntitled: boolean;
}

/**
 * Map an ingest response — or the error thrown for a non-2xx — onto the cursor
 * contract.
 *
 * Pure and exported so every branch is testable without a server. Note the two
 * "we stored nothing" answers that must NOT be conflated: `entitled: false` is
 * a settled 200 the cursor advances past, while a 500 holds.
 */
export function classifyIngestResult(result: unknown, error?: unknown): IngestOutcome {
  if (error) {
    if (error instanceof ApiError) {
      if (error.status === 404) return { kind: 'agent_unbound' };

      // An OVERSIZE 400 is not the same as a malformed one, and the difference
      // decides whether 500 valid calls survive.
      //
      // Oversize means the FRAMING was wrong and the data is fine — a
      // correctly-chunked retry stores every row. Advancing past it discards
      // them permanently, because the transcript is gone at 7 days. So it
      // holds, matching ENG-8574 AC1 ("a 500 and an oversize 400 both leave the
      // cursor unmoved") rather than the issue's own disposition table, which
      // lumps all 4xx together.
      //
      // Matched on a STABLE CODE, not on the message. ApiError surfaces
      // `body.error`, so the route's `message` never reaches `error.message` —
      // an earlier prose regex here was dead code in production and silently
      // took the advance-and-discard branch instead. The test passed only
      // because it hand-built a body the route does not send. (CodeRabbit.)
      //
      // The "do not spin" concern that table raises is real but is answered
      // structurally instead: the host always chunks to MAX_CALLS_PER_POST, and
      // that constant is pinned to the route's by test — so the only way to see
      // this is a cap that drifted, which CI fails on. A loud, recoverable spin
      // is the right trade against a silent, permanent discard.
      if (error.status === 400 && error.body?.['code'] === 'batch_too_large') {
        return { kind: 'ingest_failed', reason: `oversize batch (HTTP 400): ${error.message}` };
      }

      // Any other 4xx is a producer bug: the payload will never be accepted, so
      // retrying it forever wedges every later call in the file behind it.
      if (error.status >= 400 && error.status < 500) {
        return { kind: 'batch_rejected', reason: `HTTP ${error.status}: ${error.message}` };
      }
      return { kind: 'ingest_failed', reason: `HTTP ${error.status}: ${error.message}` };
    }
    // A transport error (DNS, timeout, connection reset) is NOT evidence the
    // write failed to happen, but it is also not evidence it succeeded. Hold —
    // the ingest upsert is idempotent, so a redundant retry costs nothing while
    // a wrong advance costs the records permanently.
    return { kind: 'ingest_failed', reason: String((error as Error)?.message ?? error) };
  }

  const body = (result ?? {}) as Record<string, unknown>;
  if (body['entitled'] === false) return { kind: 'not_entitled' };
  if (body['ok'] === false) {
    return { kind: 'ingest_failed', reason: String(body['message'] ?? 'ok:false') };
  }
  return { kind: 'ingested' };
}

/** Split into route-sized batches. */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Byte/line position of the current end of a transcript. */
function endOfFile(absPath: string): { byteOffset: number; lineOffset: number } | null {
  try {
    return { byteOffset: statSync(absPath).size, lineOffset: 0 };
  } catch {
    return null;
  }
}

export interface ScanArgs {
  api: ScanApi;
  agentId: string;
  codeName: string;
  /** `sessionTranscriptDir(projectDir)` for this agent. */
  transcriptDir: string;
  /** `~/.claude/projects`, the root every `transcript_rel_path` is relative to. */
  projectsRoot: string;
  /** Sessions to consider this tick. The caller decides which are live. */
  sessionIds: string[];
  /** Per-agent HMAC salt for path hashing. */
  pathHashSalt: string;
  /**
   * CHARTER `logging_mode: hash-only` — withhold every target.
   *
   * REQUIRED, not optional (ENG-8726). It was optional, and the sole production
   * caller never set it, so the enforcement ENG-8573 built was unreachable on
   * the fleet while reading as wired end-to-end. An optional privacy flag
   * defaults to "disclose" in exactly the callers that forgot it; making the
   * compiler ask means the next caller has to answer. Produced by
   * `readAgentLoggingMode`.
   */
  hashOnly: boolean;
  /** Scan existing transcripts from the top. Off by default — see the storm note. */
  backfill?: boolean;
  log: (msg: string) => void;
  now?: () => number;
  homeDir?: string;
  /** Test seam for the cursor file location. */
  cursorPath?: string;
  /**
   * Per-call scan budget (ENG-8740). Defaults to `WINDOW_BUDGET_BYTES`.
   *
   * A seam, not a tunable: proving the bound with the real 4 MiB default would
   * mean writing a multi-megabyte fixture per assertion, which is slow enough
   * that the tests stop being run. A small budget makes "resumes across ticks
   * with nothing skipped or double-counted" a cheap, exact assertion — and it is
   * the same code path either way, which is the property that matters.
   */
  windowBudgetBytes?: number;
}

/**
 * Scan one agent's transcripts and ship what is new.
 *
 * Never throws: this runs inside the manager tick, and an audit scanner that can
 * take the manager down with it is a worse failure than a missed scan.
 */
export async function scanAgentToolCalls(args: ScanArgs): Promise<ScanSummary> {
  const now = args.now ?? Date.now;
  const log = args.log;
  const summary: ScanSummary = {
    filesScanned: 0,
    callsExtracted: 0,
    callsIngested: 0,
    filesHeld: 0,
    coverageReported: 0,
    notEntitled: false,
  };

  const path = args.cursorPath ?? cursorStatePath(args.codeName, args.homeDir);
  const cursors = loadCursors(path);

  const extractOpts: ExtractOptions = {
    transcriptDir: args.transcriptDir,
    archive: readHostArchiveAddress(),
    redaction: { pathHashSalt: args.pathHashSalt, hashOnly: args.hashOnly },
  };

  // Every file the cursor already knows about is re-considered even if its
  // session is no longer live: a session that rolled over mid-turn still has
  // unscanned tail, and dropping it the moment it stops being current is a
  // silent gap at exactly the boundary where one is easiest to miss.
  const seen = new Set<string>();
  const files: { key: string; file: ReturnType<typeof enumerateSessionTranscripts>[number] }[] = [];
  for (const sessionId of args.sessionIds) {
    for (const f of enumerateSessionTranscripts(args.transcriptDir, sessionId, args.projectsRoot)) {
      const key = cursorKey(sessionId, f.ref);
      if (seen.has(key)) continue;
      seen.add(key);
      files.push({ key, file: f });
    }
  }

  const touched: string[] = [];

  for (const { key, file } of files) {
    let cursor = cursors.get(key);

    if (!cursor) {
      const end = endOfFile(file.absPath);
      if (!end) continue; // no such file; nothing to record
      // THE FIRST-RUN BOUND. A file we have never seen starts at its current
      // end unless backfill was asked for. started_from_line stays 0 because a
      // byte-seek gives us no line ordinal for the skipped prefix; the byte
      // companion `startedFromByte` (ENG-8740) is what records where the scan
      // really began, so the coverage row reports the empty interval
      // [seek, seek] instead of implying every byte below it was covered.
      cursor = initialCursor({
        startByte: args.backfill ? 0 : end.byteOffset,
        startLine: 0,
        transcriptRelPath: file.relPath,
        nowMs: now(),
      });
      cursors.set(key, cursor);
      touched.push(key);
      if (!args.backfill && end.byteOffset > 0) {
        log(
          `[tool-call-scan] first-seen ${file.relPath} ref=${file.ref} — starting at byte ` +
            `${end.byteOffset} (scan-from-now; set backfill to include the existing ${end.byteOffset}B)`,
        );
      }
      // Nothing to send on the first sighting when starting from the end.
      if (!args.backfill) continue;
    }

    const window = extractTranscriptWindow(file, extractOpts, {
      byteOffset: cursor.byteOffset,
      lineOffset: cursor.lineOffset,
      // ENG-8832: carry the run scope across ticks. A marker arrives once, at
      // the head of its run; without this every window after the first would
      // report NULL and the attribution would silently evaporate.
      runScopeId: cursor.runScopeId,
      ...(args.windowBudgetBytes === undefined ? {} : { budgetBytes: args.windowBudgetBytes }),
    });
    summary.filesScanned += 1;

    if (window.missing) {
      cursors.set(key, { ...cursor, lastDisposition: 'transcript_missing', lastScanAt: new Date(now()).toISOString() });
      touched.push(key);
      continue;
    }
    if (window.rewound) {
      log(`[tool-call-scan] ${file.relPath} ref=${file.ref} shrank below the cursor — re-reading from 0`);
    }
    if (window.truncated) {
      // Not a fault, and deliberately logged anyway: a host working through a
      // backlog and a host wedged on one look identical from the row counts.
      // The byte budget means a large file takes several ticks, and without a
      // line saying so the only visible symptom is a watermark that crawls.
      log(
        `[tool-call-scan] ${file.relPath} ref=${file.ref} hit the window budget at byte ` +
          `${window.endByte} — more remains, resuming next tick`,
      );
    }

    summary.callsExtracted += window.records.length;

    // Only calls the extractor could pair are shipped; the unpaired tail waits
    // for its result, because ignoreDuplicates means a null written now can
    // never be corrected. `nextCursor` owns how long that wait may last.
    const unpairedIds = new Set(window.unpaired.map((u) => u.toolUseId));
    const sendable = window.records.filter((r) => !unpairedIds.has(r.tool_use_id));

    // `ingested` as the no-batch default is right for ADVANCEMENT — with
    // nothing unsent there is nothing to hold for — but NOT for the recorded
    // disposition. See `attempted` below.
    let outcome: IngestOutcome = { kind: 'ingested' };
    let attempted = false;
    let ingested = 0;

    for (const batch of chunk(sendable, MAX_CALLS_PER_POST)) {
      attempted = true;
      const res = await postCalls(args.api, args.agentId, batch);
      outcome = res.outcome;
      ingested += res.ingested;
      if (outcome.kind === 'not_entitled') summary.notEntitled = true;
      // Stop at the first batch that must not advance. Sending the rest would
      // leave the cursor behind rows that DID land, which is safe but wasteful;
      // stopping keeps the resume point and the wire in step.
      if (outcome.kind !== 'ingested' && outcome.kind !== 'not_entitled') break;
    }

    summary.callsIngested += ingested;

    const computed = nextCursor({
      previous: cursor,
      endByte: window.endByte,
      endLine: window.endLine,
      earliestUnpaired: window.unpaired[0] ?? null,
      endRunScopeId: window.endRunScopeId,
      outcome,
      callsExtracted: sendable.length,
      nowMs: now(),
      rewound: window.rewound,
      truncated: window.truncated,
    });

    // A tick with nothing to send made no attempt, so it learned nothing about
    // how ingest is behaving. Stamping 'ok' here would assert a successful
    // ingest that never happened — and because most ticks are quiet, the very
    // next one would erase a `not_entitled` or `ingest_failed` state and leave
    // the file looking healthy. The disposition describes the last ATTEMPT, so
    // with no attempt it is carried forward unchanged.
    const advanced: FileCursor = attempted
      ? computed
      : { ...computed, lastDisposition: cursor.lastDisposition };
    if (advanced.byteOffset === cursor.byteOffset && outcome.kind === 'ingest_failed') {
      summary.filesHeld += 1;
      log(
        `[tool-call-scan] HOLDING cursor for ${file.relPath} ref=${file.ref} at byte ` +
          `${cursor.byteOffset}: ${outcome.reason}`,
      );
    }
    cursors.set(key, advanced);
    touched.push(key);

    if (outcome.kind === 'agent_unbound') {
      log(`[tool-call-scan] host is not bound to agent ${args.agentId} — stopping this tick`);
      break;
    }
  }

  // Persist BEFORE reporting coverage. If the process dies between the two, the
  // worst case is a coverage row that lags the cursor — an understatement.
  // The other order risks a coverage row claiming ground the cursor never kept.
  saveCursors(path, cursors);

  summary.coverageReported = await reportCoverage(args, cursors, touched, log);
  return summary;
}

/** POST one batch and classify the answer. */
async function postCalls(
  api: ScanApi,
  agentId: string,
  calls: ToolCallRecord[],
): Promise<{ outcome: IngestOutcome; ingested: number }> {
  try {
    const res = await api.post<{ ok?: boolean; entitled?: boolean; ingested?: number }>(
      '/host/tool-calls',
      { agent_id: agentId, calls },
    );
    const outcome = classifyIngestResult(res);
    return { outcome, ingested: typeof res?.ingested === 'number' ? res.ingested : 0 };
  } catch (err) {
    return { outcome: classifyIngestResult(undefined, err), ingested: 0 };
  }
}

/**
 * Report the watermark for every file touched this tick.
 *
 * Best-effort and deliberately AFTER the cursor is persisted: coverage is a
 * report about scanning, and failing to file the report must never change what
 * was scanned.
 */
async function reportCoverage(
  args: ScanArgs,
  cursors: Map<string, FileCursor>,
  touched: string[],
  log: (m: string) => void,
): Promise<number> {
  const rows: CoverageRow[] = [];
  for (const key of new Set(touched)) {
    const c = cursors.get(key);
    if (c) rows.push(coverageRowFor(key, c, { extractor: EXTRACTOR_VERSION, redaction: REDACTION_RULE_VERSION }));
  }
  if (rows.length === 0) return 0;

  let reported = 0;
  for (const batch of chunk(rows, MAX_COVERAGE_ROWS_PER_POST)) {
    try {
      await args.api.post('/host/tool-calls/coverage', { agent_id: args.agentId, coverage: batch });
      reported += batch.length;
    } catch (err) {
      // Never fatal: a missing coverage row understates what was scanned, which
      // is the safe direction. Logged so a persistently failing report is
      // visible rather than looking like an agent that stopped being scanned.
      log(`[tool-call-scan] coverage report failed (${batch.length} rows): ${String(err)}`);
      break;
    }
  }
  return reported;
}
